/**
 * The player's rudder, deflected on his own aeroplane.
 *
 * Presentation only, on `flight/propeller-blade-spin.ts`'s terms: nothing here
 * reads or writes hashed simulation state, nothing here is stepped by the fixed
 * clock, and a replay of the same tape draws the same rudder because the angle
 * is a pure function of the input the tape carries. The rudder is a *surface*,
 * not a force — the flight model's yaw response is unchanged and unaware of it.
 *
 * ## Three things this had to get right, all of them written down before it existed
 *
 * `docs/engineering/cockpit-3d-design.md` §16.2 priced this feature and left the
 * next lane three traps. They are the whole design of this file:
 *
 * 1. **Read `cockpitInputs`, never `inputs`.** `scene-loop.ts` line 332:
 *    `const cockpitInputs = state.runner.activeInputs?.() ?? inputs`. A playback
 *    runner ignores the `inputs` handed to `advance` and flies the tape's own
 *    stick, so a surface driven off the live stick would sit dead through a
 *    `/vcr` replay and twitch to whatever the viewer's keyboard was doing. The
 *    `?.` means every live runner falls through to the same object, so this is
 *    not a special case — it is the correct source in both modes.
 *
 * 2. **Do not use `SimState.rates`.** That is the achieved body yaw rate *after*
 *    actuator lag, so it is non-zero in an uncommanded spin with the pilot's feet
 *    flat on the floor — a rudder that deflects itself while the aeroplane
 *    departs. `FlightInputs.yaw` is what his feet are doing, which is what a
 *    rudder shows.
 *
 * 3. **The hinge axis is authored, not derived.** That one belongs to
 *    `three/enhanced-nodes.ts`'s `RUDDER_HINGE_AXIS`, which says why.
 *
 * ## And one the AI fleet is not getting yet
 *
 * This is **player-only**, deliberately. An AI aircraft's stick lives in
 * `Decision.inputs` and is discarded after the decision is applied; carrying it
 * to the presentation layer means putting it on `CombatAircraft`, which is
 * hashed lockstep state and therefore a multiplayer determinism review rather
 * than a cockpit change. §16.2 costed that at 4–6 lane-hours of its own and it
 * is queued, not skipped. Until then every other aeroplane in the sky wears a
 * centred rudder, which is exactly what it wore before this file existed.
 */

import type { FlightInputs } from "@red-opus/engine";
import type * as THREE from "three/webgpu";

import { turnModelledRudders } from "../three/enhanced-nodes.ts";

/**
 * Full-scale rudder travel, in radians — 25°.
 *
 * A period rudder's stops are between 20° and 30° either side and this port has
 * no measurement of its own: the source model draws the surface undeflected and
 * `SHADES.DAT` has nothing to say about hinges. So it is a stated number, kept
 * at the conservative middle of that range for the reason a too-*large* travel
 * is worse than a too-small one — at full deflection the rudder's own trailing
 * edge swings `sin(angle) x chord` sideways, and past about 30° that reads as a
 * surface that has come off its hinges rather than one that has moved.
 */
export const RUDDER_TRAVEL_RAD = (25 * Math.PI) / 180;

/**
 * How far a rudder deflects for a given pedal input.
 *
 * Linear and clamped, with **no dead zone**: `cockpit-frame.ts`'s
 * `DEFLECTION_DEADZONE` of 0.35 exists because the painted `.TTM` surfaces have
 * only three frames — left, centre, right — and a three-position display needs a
 * threshold to sit still at. A modelled surface has every angle available to it,
 * so quantising one would be throwing away the only thing it has over the
 * painting.
 *
 * Sign: `FlightInputs.yaw` is −1 for full *left* rudder, and the hinge axis is
 * the model's own +Z (up) after Y-up conversion, about which a positive rotation
 * swings the rudder's trailing edge to the aeroplane's... left. So the two agree
 * and the mapping is a straight multiply — but that is a fact about this
 * exporter's frame, and `rudder-deflection.test.ts` measures it on a real `.glb`
 * rather than trusting this sentence.
 */
export function rudderAngleOf(yaw: number): number {
  return Math.max(-1, Math.min(1, Number.isFinite(yaw) ? yaw : 0)) * RUDDER_TRAVEL_RAD;
}

/**
 * Deflects the player's modelled rudder to match his pedals.
 *
 * `root` is the **pose group** (`SceneState.aircraft`), never the mesh group:
 * ENHANCED MODELS empties and refills `aircraftModel` under it, and anything
 * that addressed the mesh directly would lose its target on the next toggle.
 * There is no state to keep — the angle is a function of the current input, so
 * unlike the propeller's phase nothing has to survive that swap.
 *
 * Returns how many rudder nodes it found: zero on the nineteen airframes whose
 * source does not separate the surface, and zero on the authentic bake, both of
 * which are ordinary answers rather than faults.
 */
export function deflectPlayerRudder(root: THREE.Object3D, inputs: FlightInputs): number {
  return turnModelledRudders(root, rudderAngleOf(inputs.yaw));
}
